import * as Yup from 'yup';
import { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';

import { useTheme } from '@mui/material/styles';
import { Link, Stack, TextField, IconButton, InputAdornment, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import Iconify from '../../components/Iconify';
import { setUser } from '../../store/user/userSlice';

// ----------------------------------------------------------------------

export default function LoginForm({ onNotConfirmedUser }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: (data, { setSubmitting }) => {
      const { email, password } = data;
      Auth.signIn({
        username: email,
        password,
        attributes: {
          email,
        },
      })
        .then(async (res) => {
          await Auth.currentSession().then((res) => {
            const idToken = res.getIdToken();
            localStorage.setItem('token', idToken?.jwtToken);
          });
          const user = {
            firstName: res?.attributes?.['custom:firstName'],
            lastName: res?.attributes?.['custom:lastName'],
            sn: res?.attributes?.['custom:sn']?.toLowerCase(),
            provisioned: res?.attributes?.['custom:provisioned'],
            email: res?.attributes?.email,
          };

          dispatch(setUser(user));
          localStorage.setItem('user', JSON.stringify(user));

          navigate('/dashboard/devices-list', { replace: true });
        })
        .catch((err) => {
          if (err.message === 'User is not confirmed.') {
            onNotConfirmedUser({ email, password });
          } else {
            enqueueSnackbar(err.message, {
              variant: 'error',
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Sign in to Dashboard
      </Typography>

      <Typography sx={{ color: 'text.secondary', mb: 5 }}>Enter your details below.</Typography>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              autoComplete="username"
              type="email"
              label="Email address"
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ my: 2 }}>
            <Link
              component={RouterLink}
              variant="subtitle2"
              to="/restore-password"
              underline="hover"
              sx={{ color: theme.palette.primary.darker }}
            >
              Forgot password?
            </Link>
          </Stack>

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
            Login
          </LoadingButton>
        </Form>
      </FormikProvider>
    </>
  );
}
